import React, { useEffect } from "react";
import { useToast } from "../../store/ToastProvider";
import {
  StyledTable,
  TableContainer,
} from "../../components/tables/styledComponents";
import {
  Card,
  RowContainer,
} from "../../components/containers/styledComponents";
import { CompassParagraph } from "../../components/common/styledComponents";
import { usePost } from "../../hooks/usePost";
import {
  EngagementScreenData,
  StewardshipFilterParam,
} from "../../models/stewardshipModels";
import {
  STEWARDSHIP_SCREENER_BASE_COLUMNS,
  STEWARDSHIP_SCREENER_COLUMN_TYPES,
  STEWARDSHIP_SCREENER_PAGE_SIZE,
} from "./constants";
import { TABLE_BASE_HEIGHT } from "../../styles";
import { CellType } from "../../models/tableModels";
import { CustomTableCell } from "../../components/tables/CustomTableCell";
import { Filter } from "../../types/PageFilterTypes";
import { PaginationParams } from "../../types/ScreenerTypes";
import {
  buildStewardShipScreenerDataSource,
  generateFakeEngagementData,
  getColumnTypeFromKeyOrValue,
} from "./utils";
import { MESSAGES } from "../../messages";

export const EngagementScreenerTable: React.FC<{
  filterParams: StewardshipFilterParam;
  fundOptions: Filter[];
  managerOptions: Filter[];
  totalRecords: number;
  areOptionsLoading?: boolean;
  handlePagination: (pagination: PaginationParams) => void;
}> = ({
  filterParams,
  fundOptions,
  managerOptions,
  totalRecords,
  areOptionsLoading = false,
  handlePagination,
}) => {
  const {
    data: engagementData,
    sendData: getEngagementData,
    isLoading,
    error: getEngagementDataError,
  } = usePost<EngagementScreenData[], StewardshipFilterParam>();
  const { addToast } = useToast();

  useEffect(() => {
    getEngagementData("GET_STEWARDSHIP_SCREENER_DATA", filterParams);
  }, [filterParams]);

  useEffect(() => {
    if (getEngagementDataError) {
      addToast(
        MESSAGES.SCREENING_TOOL.ERROR_FETCHING_ENGAGEMENT_RECORDS,
        "error",
      );
    }
  }, [getEngagementDataError]);

  return (
    <Card h="auto" m="0 20px" isLoading={isLoading}>
      <RowContainer>
        <CompassParagraph fontSize={16} bold={true}>
          Engagement Screener
        </CompassParagraph>
      </RowContainer>
      <TableContainer p="10px">
        <StyledTable
          isLoading={isLoading}
          dataSource={buildStewardShipScreenerDataSource(
            engagementData || generateFakeEngagementData(),
            fundOptions,
            managerOptions,
            areOptionsLoading || isLoading || !engagementData,
          )}
          hasEveryCellBorderBottom={true}
          scroll={{ y: TABLE_BASE_HEIGHT, x: "max-content" }}
          pagination={{
            pageSize: STEWARDSHIP_SCREENER_PAGE_SIZE,
            total: totalRecords,
            showSizeChanger: false,
            current: filterParams?.pagination?.page,
            showTotal: (total, range) =>
              `Showing ${range[0]}-${range[1]} of ${total} records`,
          }}
          onChange={(pagination): void =>
            handlePagination({
              page: pagination.current as number,
              page_size: pagination.pageSize as number,
            })
          }
        >
          {STEWARDSHIP_SCREENER_BASE_COLUMNS.map((column) => (
            <StyledTable.Column
              key={column.key}
              dataIndex={column.dataIndex}
              title={column.title}
              fixed={column.fixed}
              render={(value: CellType): JSX.Element => (
                <CustomTableCell
                  value={value}
                  type={
                    getColumnTypeFromKeyOrValue(
                      column.key as string,
                      value,
                      STEWARDSHIP_SCREENER_COLUMN_TYPES,
                      isLoading,
                    ).type
                  }
                />
              )}
            />
          ))}
        </StyledTable>
      </TableContainer>
    </Card>
  );
};
